import { APIClient, getLoggedinUser } from "../helpers/api_helper";
import * as url from "../helpers/url_helper";
import type { PaginatedResponse } from "../types/api";

const api = new APIClient();

// Helper function to get tenantId from logged-in user
const getTenantId = (): string | null => {
    const authUser = getLoggedinUser();
    return authUser?.tenantId || null;
};

// Interface for API Response & Frontend Use (matches Swagger Inventory schema)
export interface StockItem {
    id: string;
    tenantId: string;
    tenantName?: string;
    productId: string;
    productName?: string;
    productCode?: string;
    tenantLocationId: string;
    tenantLocationName?: string;
    currentStock: number;
    stockOnRent: number;
    quantityDamaged: number;
    quantityLost: number;
    minStockLevel?: number;
    maxStockLevel?: number;
    reorderPoint?: number;
    reorderQuantity?: number;
    utilization?: number;
    lastRestockedDate?: string;
    lastIssuedDate?: string;
    notes?: string;
    isLowStock: boolean;
    isDeleted: boolean;
    created?: string;
    modified?: string;
}

// Interface for API Create Request (matches Swagger InventoryCreateRequest)
export interface StockItemCreatePayload {
    tenantId?: string; // Optional - backend may extract from JWT token
    productId: string;
    tenantLocationId: string;
    currentStock?: number;
    stockOnRent?: number;
    quantityDamaged?: number;
    quantityLost?: number;
    minStockLevel?: number;
    maxStockLevel?: number;
    reorderPoint?: number;
    reorderQuantity?: number;
    notes?: string;
}

// Interface for API Update Request (matches PUT API specification)
export interface StockItemUpdatePayload {
    tenantLocationId: string;
    currentStock: number;
    minStockLevel: number;
    maxStockLevel: number;
    reorderPoint: number;
    reorderQuantity: number;
    notes?: string;
}

// Service Methods
export const getStockItems = (
    pageNumber: number = 1,
    pageSize: number = 200,
    tenantId?: string | null,
    location?: string | null,
    lowStockOnly?: boolean
): Promise<PaginatedResponse<StockItem>> => {
    // Use GET /Inventory endpoint with pagination - v2.0 API structure
    // Returns PaginatedResponse<StockItem> with items, totalCount, pageNumber, pageSize
    // Returns 204 NoContent when no items found (handled by axios interceptor)
    const queryParams: Record<string, string | number | boolean | undefined> = {
        pageNumber,
        pageSize,
    };

    // Add optional filters if provided
    if (lowStockOnly !== undefined && lowStockOnly) {
        queryParams.isLowStock = true;
    }

    // Note: Backend filters by tenantId from JWT token automatically
    // Location filtering handled client-side if specified
    
    return api.get<PaginatedResponse<StockItem>>(url.GET_STOCK_ITEMS, queryParams).then((response) => {
        // Handle 204 NoContent (already handled by axios interceptor, but ensure structure)
        if (!response || !response.items) {
            return {
                items: [],
                pageNumber: 1,
                pageSize: pageSize,
                totalCount: 0,
                totalPages: 0,
            } as PaginatedResponse<StockItem>;
        }

        // Filter by location client-side if specified
        let items = response.items || [];
        if (location) {
            items = items.filter((item) => 
                item.tenantLocationName?.toLowerCase().includes(location.toLowerCase())
            );
        }

        // Return paginated response matching v2.0 API structure
        return {
            items: items,
            pageNumber: response.pageNumber || pageNumber,
            pageSize: response.pageSize || pageSize,
            totalCount: location ? items.length : (response.totalCount || 0),
            totalPages: response.totalPages !== undefined 
                ? response.totalPages 
                : Math.ceil((location ? items.length : (response.totalCount || 0)) / (response.pageSize || pageSize)),
            // Optional fields - only include if present in response
            hasPreviousPage: response.hasPreviousPage,
            hasNextPage: response.hasNextPage,
        } as PaginatedResponse<StockItem>;
    });
};

export const getStockItemById = async (id: string, tenantId?: string | null): Promise<StockItem> => {
    // Simple GET call - same pattern as Lead management
    // Use GET /Inventory/{id} endpoint directly
    const response = await api.get<StockItem>(`${url.GET_INVENTORY_BY_ID}/${id}`);
    
    // Handle wrapped response if present
    if (response && typeof response === 'object' && 'result' in response) {
        return (response as any).result as StockItem;
    }
    
    return response as StockItem;
};

export const addNewStockItem = async (stockItem: StockItemCreatePayload): Promise<StockItem> => {
    // v2.0 API: POST /api/v1/Inventory
    // Response: { isSuccess: true, message: "...", result: { id, currentStock, productName, tenantLocationName } }
    const tenantId = stockItem.tenantId || getTenantId();

    if (!stockItem.productId) {
        return Promise.reject(new Error("Product ID is required"));
    }

    if (!stockItem.tenantLocationId) {
        return Promise.reject(new Error("Tenant Location ID is required"));
    }

    // Format payload according to v2.0 API specification
    const payload: any = {
        productId: stockItem.productId,
        tenantLocationId: stockItem.tenantLocationId,
        currentStock: stockItem.currentStock,
        minStockLevel: stockItem.minStockLevel,
        maxStockLevel: stockItem.maxStockLevel,
        reorderPoint: stockItem.reorderPoint,
        reorderQuantity: stockItem.reorderQuantity,
        notes: stockItem.notes,
    };

    // Only include tenantId if we have it (backend may extract from JWT)
    if (tenantId) {
        payload.tenantId = tenantId;
    }

    const response = await api.create<{ isSuccess: boolean; message: string; result: StockItem }>(url.ADD_NEW_STOCK_ITEM, payload);
    
    // Handle wrapped response - v2.0 API structure
    if (response?.isSuccess && response.result) {
        return response.result;
    }

    throw new Error(response?.message || "Failed to create inventory");
};

export const updateStockItem = async (id: string, stockItem: StockItemUpdatePayload): Promise<StockItem> => {
    // v2.0 API: PUT /api/v1/Inventory/{id}
    // Response: { isSuccess: true, message: "...", result: { id, minStockLevel, maxStockLevel, ... } }
    const payload: StockItemUpdatePayload = {
        tenantLocationId: stockItem.tenantLocationId,
        currentStock: stockItem.currentStock,
        minStockLevel: stockItem.minStockLevel,
        maxStockLevel: stockItem.maxStockLevel,
        reorderPoint: stockItem.reorderPoint,
        reorderQuantity: stockItem.reorderQuantity,
        notes: stockItem.notes,
    };

    const response = await api.put<{ isSuccess: boolean; message: string; result: StockItem }>(url.UPDATE_STOCK_ITEM + "/" + id, payload);
    
    // Handle wrapped response - v2.0 API structure
    if (response?.isSuccess && response.result) {
        return response.result;
    }

    throw new Error(response?.message || "Failed to update inventory");
};

export const deleteStockItem = async (id: string): Promise<void> => {
    // v2.0 API: DELETE /api/v1/Inventory/{id}
    // Response: { isSuccess: true, message: "Inventory deleted successfully" }
    const response = await api.delete<{ isSuccess: boolean; message: string }>(`${url.DELETE_STOCK_ITEM}/${id}`);
    
    // Handle wrapped response - v2.0 API structure
    if (response && !response.isSuccess) {
        throw new Error(response.message || "Failed to delete inventory");
    }
};

// Inventory operations (receive, issue, adjust)
export interface ReceiveStockPayload {
    tenantId?: string; // Optional - backend may extract from JWT token
    productId: string;
    tenantLocationId: string;
    quantity: number;
    purchaseOrderId?: string;
    referenceNumber?: string;
    notes?: string;
}

export interface IssueStockPayload {
    tenantId?: string; // Optional - backend may extract from JWT token
    productId: string;
    tenantLocationId: string;
    quantity: number;
    workOrderId?: string;
    scaffoldId?: string;
    notes?: string;
}

export interface AdjustStockPayload {
    tenantId?: string; // Optional - backend may extract from JWT token
    productId: string;
    tenantLocationId: string;
    quantity: number; // Must be positive - adjustmentType determines Add/Remove
    adjustmentType: 1 | 2; // 1 = Add, 2 = Remove
    reason?: string; // Deprecated - kept for backward compatibility
    adjustmentReason?: string; // New field for adjustment reason from dropdown
    notes?: string;
}

export const receiveStock = async (payload: ReceiveStockPayload): Promise<string> => {
    const tenantId = payload.tenantId || getTenantId();

    const requestPayload: any = {
        productId: payload.productId,
        tenantLocationId: payload.tenantLocationId,
        quantity: payload.quantity,
    };

    if (tenantId) {
        requestPayload.tenantId = tenantId;
    }

    if (payload.purchaseOrderId) {
        requestPayload.purchaseOrderId = payload.purchaseOrderId;
    }

    if (payload.referenceNumber) {
        requestPayload.referenceNumber = payload.referenceNumber;
    }

    if (payload.notes) {
        requestPayload.notes = payload.notes;
    }

    const response = await api.create<{ isSuccess: boolean; message: string; result: string }>(url.RECEIVE_STOCK, requestPayload);

    if (response?.isSuccess && response.result) {
        return response.result;
    }

    if (typeof response === 'string') {
        return response;
    }

    throw new Error(response?.message || "Failed to receive stock");
};

export const issueStock = async (payload: IssueStockPayload): Promise<string> => {
    const tenantId = payload.tenantId || getTenantId();

    if (!payload.productId) {
        throw new Error("Product ID is required");
    }

    if (!payload.tenantLocationId) {
        throw new Error("Location ID is required");
    }

    if (!payload.quantity || payload.quantity <= 0) {
        throw new Error("Quantity must be greater than 0");
    }

    const requestPayload: any = {
        productId: payload.productId,
        tenantLocationId: payload.tenantLocationId,
        quantity: payload.quantity,
    };

    // Always include tenantId if available
    if (tenantId) {
        requestPayload.tenantId = tenantId;
    }

    // Only include optional fields if they have values (not empty strings or undefined)
    if (payload.workOrderId && typeof payload.workOrderId === 'string' && payload.workOrderId.trim() !== "") {
        requestPayload.workOrderId = payload.workOrderId.trim();
    }

    if (payload.scaffoldId && typeof payload.scaffoldId === 'string' && payload.scaffoldId.trim() !== "") {
        requestPayload.scaffoldId = payload.scaffoldId.trim();
    }

    if (payload.notes && typeof payload.notes === 'string' && payload.notes.trim() !== "") {
        requestPayload.notes = payload.notes.trim();
    }

    const response = await api.create<{ isSuccess: boolean; message: string; result: string }>(url.ISSUE_STOCK, requestPayload);

    if (response?.isSuccess && response.result) {
        return response.result;
    }

    if (typeof response === 'string') {
        return response;
    }

    throw new Error(response?.message || "Failed to issue stock");
};

export const adjustStock = async (payload: AdjustStockPayload): Promise<string> => {
    const tenantId = payload.tenantId || getTenantId();

    if (!payload.adjustmentType || (payload.adjustmentType !== 1 && payload.adjustmentType !== 2)) {
        throw new Error("Adjustment type is required (1 = Add, 2 = Remove)");
    }

    if (!payload.quantity || payload.quantity <= 0) {
        throw new Error("Quantity must be greater than 0");
    }

    const requestPayload: any = {
        productId: payload.productId,
        tenantLocationId: payload.tenantLocationId,
        quantity: payload.quantity,
        adjustmentType: payload.adjustmentType, // 1 = Add, 2 = Remove
    };

    if (tenantId) {
        requestPayload.tenantId = tenantId;
    }

    // Send adjustmentReason if provided, otherwise fall back to reason for backward compatibility
    if (payload.adjustmentReason && payload.adjustmentReason.trim() !== "") {
        // Convert to number since API expects numeric enum value (e.g., 1, 2, 3, 4, 5)
        const reasonValue = payload.adjustmentReason.trim();
        const numericValue = Number(reasonValue);
        // Send as number if valid, otherwise send as string
        requestPayload.adjustmentReason = !isNaN(numericValue) ? numericValue : reasonValue;
    } else if (payload.reason && payload.reason.trim() !== "") {
        requestPayload.reason = payload.reason.trim();
    }

    if (payload.notes && payload.notes.trim() !== "") {
        requestPayload.notes = payload.notes.trim();
    }

    const response = await api.create<{ isSuccess: boolean; message: string; result: string }>(url.ADJUST_STOCK, requestPayload);

    // Check if response indicates success
    if (response?.isSuccess && response.result) {
        return response.result;
    }

    // If response is a string, return it
    if (typeof response === 'string') {
        return response;
    }

    // If isSuccess is false, extract the message from response
    // API returns: { isSuccess: false, message: "Insufficient damaged quantity to recover. Current damaged: 5, Requested: 8", ... }
    if (response?.message) {
        // Debug logging
        console.log("adjustStockApi - API returned isSuccess=false, message:", response.message);
        throw new Error(response.message);
    }

    // Debug logging
    console.log("adjustStockApi - No message in response, response:", response);
    throw new Error("Failed to adjust stock");
};

// Stock Movement History interface
export interface StockMovement {
    id: string;
    productId: string;
    productCode: string;
    productName: string;
    movementType: 1 | 2 | 3; // 1 = In, 2 = Out, 3 = Adjust
    movementTypeName: string;
    quantity: number;
    location: string;
    movementDate: string;
    workOrderId?: string | null;
    workOrderNumber?: string | null;
    scaffoldId?: string | null;
    scaffoldName?: string | null;
    purchaseOrderId?: string | null;
    purchaseOrderNumber?: string | null;
    referenceNumber?: string | null;
    adjustmentReason?: number | string | null; // Adjustment reason enum value or display name
    adjustmentReasonName?: string | null; // Human-readable adjustment reason name
    performedBy: string;
    notes?: string;
}

// Inventory Dashboard interface
export interface InventoryDashboard {
    totalProducts: number;
    totalStockValue: number;
    lowStockItems: number;
    totalStock: number;
    stockOnRent: number;
    quantityDamaged: number;
    quantityLost: number;
}

/**
 * Get Stock Movement History for a product
 * @param productId - Product ID
 * @param startDate - Optional start date filter (ISO date string)
 * @param endDate - Optional end date filter (ISO date string)
 * @returns Promise with array of stock movements
 */
export const getStockMovements = async (
    productId: string,
    startDate?: string,
    endDate?: string
): Promise<StockMovement[]> => {
    if (!productId) {
        return Promise.reject(new Error("Product ID is required"));
    }

    const queryParams: Record<string, string | undefined> = {};
    if (startDate) {
        queryParams.startDate = startDate;
    }
    if (endDate) {
        queryParams.endDate = endDate;
    }

    const response = await api.get<StockMovement[]>(`${url.GET_STOCK_MOVEMENTS}/${productId}`, queryParams);
    return Array.isArray(response) ? response : [];
};

/**
 * Get Inventory Dashboard summary
 * @param locationId - Optional location ID filter
 * @returns Promise with dashboard summary
 */
export const getInventoryDashboard = async (locationId?: string): Promise<InventoryDashboard> => {
    const queryParams: Record<string, string | undefined> = {};
    if (locationId) {
        queryParams.locationId = locationId;
    }

    const response = await api.get<InventoryDashboard>(url.GET_INVENTORY_DASHBOARD, queryParams);
    return response;
};

/**
 * Get Low Stock Items for a tenant
 * @param tenantId - Tenant ID (optional, will use logged-in user's tenantId if not provided)
 * @returns Promise with array of low stock items
 */
export const getLowStockItems = async (tenantId?: string | null): Promise<StockItem[]> => {
    // v2.0 API: Use GET /Inventory endpoint with isLowStock filter
    // Returns PaginatedResponse<StockItem> or 204 NoContent
    const response = await api.get<PaginatedResponse<StockItem>>(url.GET_STOCK_ITEMS, {
        pageNumber: 1,
        pageSize: 200,
        isLowStock: true,
    });
    
    // Handle 204 NoContent (already handled by axios interceptor)
    return response?.items || [];
};
